import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function ArrowIcon() {
  return (
    <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
  );
}

export function ThankYouContent() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('thankyoucontent.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('thankyoucontent.secondary_cta', secondaryCtaDef);
  const color4 = useColor4();

  return (
    <section ref={sectionRef} className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
        />

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <EditableSlotGroup groupPath="thankyoucontent.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/floor-plans'}
              target={primaryCta._target || '_self'}
              style={color4?.primaryOverride}
              className="thankyou-cta-primary group/cta relative inline-flex items-center justify-center gap-2.5 px-10 py-4 min-w-[240px] text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{primaryCta.label || 'View Floor Plans'}</span>
              <ArrowIcon />
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="thankyoucontent.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={secondaryCta.url || '/schedule-a-tour'}
              target={secondaryCta._target || '_self'}
              style={color4?.primaryOverride}
              className="thankyou-cta-secondary group/cta relative inline-flex items-center justify-center gap-2.5 px-10 py-4 min-w-[240px] text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-primary)] border-2 border-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{secondaryCta.label || 'Schedule a Tour'}</span>
              <ArrowIcon />
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .thankyou-cta-primary {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .thankyou-cta-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .thankyou-cta-primary:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .thankyou-cta-primary:hover::before {
          left: 100%;
        }
        .thankyou-cta-secondary {
          border-radius: 2px;
        }
        .thankyou-cta-secondary:hover {
          box-shadow:
            0 4px 12px rgba(0,0,0,0.15),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-2px);
        }
        .thankyou-cta-primary:active,
        .thankyou-cta-secondary:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.primary_cta': primaryCtaDef,
    'thankyoucontent.secondary_cta': secondaryCtaDef,
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
